// NewsletterOptIn — single UNCHECKED, optional "Send me The Weekly Close" box.
// Posts on its own to /api/public/subscriptions/optin (typeSlug 'newsletter'),
// so ticking writes a subscribed row with consent evidence naming the page, and
// not ticking changes nothing. Same contract as MarketingOptIn.tsx. Wording and
// the list of sign-ups that show it live in lib/newsletterOptIn.ts.

import { useState } from 'react';
import type { CSSProperties } from 'react';
import { NEWSLETTER_OPTIN_LABEL, NEWSLETTER_OPTIN_DONE } from '../lib/newsletterOptIn';

const API_BASE = (import.meta as unknown as { env: Record<string, string> }).env?.VITE_API_BASE_URL ?? '';

interface Props {
  email: string | null;
  firstName?: string | null;
  lastName?: string | null;
  source: 'registration' | 'call-rsvp';
  /** The event or call slug. */
  sourceDetail: string;
  page: 'sign-up' | 'thank-you';
  style?: CSSProperties;
}

export function NewsletterOptIn({ email, firstName, lastName, source, sourceDetail, page, style }: Props) {
  const [checked, setChecked] = useState(false);
  const [status, setStatus] = useState<'idle' | 'saving' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);

  if (!email) return null;

  const onChange = async (next: boolean) => {
    setChecked(next);
    setError(null);
    if (!next) return;
    setStatus('saving');
    try {
      const res = await fetch(`${API_BASE}/api/public/subscriptions/optin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email, firstName: firstName ?? null, lastName: lastName ?? null,
          source, sourceDetail, page, typeSlug: 'newsletter',
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.ok) {
        setStatus('error'); setChecked(false);
        setError('Could not save your preference. Please try again.');
        return;
      }
      setStatus('done');
    } catch {
      setStatus('error'); setChecked(false);
      setError('Network error. Please try again.');
    }
  };

  if (status === 'done') {
    return (
      <div style={{ marginTop: 12, ...style }} data-newsletter-optin="done">
        <span style={{ fontSize: 13 }}>{NEWSLETTER_OPTIN_DONE}</span>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12, ...style }} data-newsletter-optin="form">
      <label style={row}>
        <input type="checkbox" checked={checked} disabled={status === 'saving'}
          onChange={(e) => void onChange(e.target.checked)} />
        <span>{NEWSLETTER_OPTIN_LABEL}</span>
      </label>
      {error && <div style={{ color: '#c0392b', fontSize: 12, marginTop: 4 }}>{error}</div>}
    </div>
  );
}

const row: CSSProperties = { display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, cursor: 'pointer', lineHeight: 1.4 };
